import {Injectable} from '@angular/core';
import ExcelJS from 'exceljs';
import {IDSFacet, IDSRule, IDSSpecification, IDSValueField, isValueFieldEmpty, NecessityType} from '../models/ids.model';

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const IDS_TEMPLATE_URL = 'https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/IDS-Check_Vorlage.xlsx';
const ATTRIBUTE_LIST_TEMPLATE_URL = 'https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/IDS-Check_Attributliste.xlsx';

/** Sheets der Attributliste-Vorlage, die keine Bauteil-Tabelle enthalten und nie befüllt werden. */
const ATTRIBUTE_LIST_NON_COMPONENT_SHEETS = new Set(['Deckblatt', 'Kürzel', 'Anleitung']);

const NECESSITY_TEXT: Record<NecessityType, string> = {
  required: 'Erforderlich',
  optional: 'Optional',
  prohibited: 'Verboten',
};

interface AttributeSheetColumns {
  startRow: number;
  attribute: number;
  dataType: number;
  pset: number;
  optionset: number;
  loiColumns: number[];
}

/**
 * Erzeugt aus dem aktuellen IDS-Regelwerk befüllte Excel-Arbeitsmappen für die beiden
 * offiziellen IDS-Excel-Vorlagen ("IDS-Vorlage" und "Attributliste", von S3 geladen – siehe
 * `IDS_TEMPLATE_URL`/`ATTRIBUTE_LIST_TEMPLATE_URL`, dieselben Dateien wie beim Vorlagen-Download
 * im Import-Popup). Es wird bewusst NICHT bei Null ein neues Workbook gebaut: die echten
 * Vorlagen tragen Formatierung, Spaltenbreiten, Beispieltexte und Dropdown-Validierungen, die ein
 * selbst gebautes Workbook nicht hätte. Nur die (Beispiel-)Datenzeilen werden geleert und mit den
 * echten Werten aus `rule` überschrieben; Kopfzeilen/Formatierung bleiben unangetastet.
 *
 * Spalten-/Zeilenlayout ist 1:1 an `IdsExcelImportService` angelehnt, damit ein Export
 * anschließend wieder importierbar ist.
 */
@Injectable({providedIn: 'root'})
export class IdsExcelExportService {

  // ═══════════ IDS-Vorlage (.xlsx) ═══════════

  async exportIdsTemplate(rule: IDSRule): Promise<Blob> {
    const workbook = await this.loadTemplate(IDS_TEMPLATE_URL);

    const meta = this.requireSheet(workbook, 'Metadaten');
    this.setCell(meta, 6, 3, rule.info.title ?? '');
    this.setCell(meta, 7, 3, rule.info.author ?? null);
    this.setCell(meta, 8, 3, rule.info.description ?? null);
    this.setCell(meta, 9, 3, rule.info.version ?? null);
    this.setCell(meta, 10, 3, rule.info.date ? this.parseIsoDate(rule.info.date) ?? null : null);
    this.setCell(meta, 11, 3, rule.info.purpose ?? null);
    this.setCell(meta, 12, 3, rule.info.milestone ?? null);
    this.setCell(meta, 13, 3, rule.info.copyright ?? null);

    const applicability = this.requireSheet(workbook, 'Anwendbarkeit');
    const requirements = this.requireSheet(workbook, 'Anforderungen');
    const applicabilityOriginalRows = applicability.rowCount;
    const requirementsOriginalRows = requirements.rowCount;
    this.clearDataRows(applicability, 9, applicabilityOriginalRows, 16);
    this.clearDataRows(requirements, 9, requirementsOriginalRows, 20);

    let appRow = 9;
    let reqRow = 9;
    for (const spec of rule.specifications) {
      for (const facet of spec.applicability) {
        if (appRow > applicabilityOriginalRows) this.copyRowStyle(applicability, 9, appRow);
        this.writeFacetRow(applicability, appRow++, spec.name, facet, false);
      }
      for (const facet of spec.requirements) {
        if (reqRow > requirementsOriginalRows) this.copyRowStyle(requirements, 9, reqRow);
        this.writeFacetRow(requirements, reqRow++, spec.name, facet, true);
      }
    }

    const specs = this.requireSheet(workbook, 'Spezifikationen');
    const specsOriginalRows = specs.rowCount;
    this.clearDataRows(specs, 7, specsOriginalRows, 7);
    rule.specifications.forEach((spec, i) => {
      const r = 7 + i;
      if (r > specsOriginalRows) this.copyRowStyle(specs, 7, r);
      this.setCell(specs, r, 2, spec.name);
      this.setCell(specs, r, 3, spec.ifcVersions.join(' '));
      this.setCell(specs, r, 4, NECESSITY_TEXT[spec.necessity] ?? 'Erforderlich');
      this.setCell(specs, r, 5, spec.description ?? null);
      this.setCell(specs, r, 6, spec.instruction ?? 'Keine');
      this.setCell(specs, r, 7, spec.identifier ?? null);
    });

    return this.toBlob(workbook);
  }

  /** Schreibt eine Facette in eine Zeile der Anwendbarkeit/Anforderungen (nur die für ihren Typ relevanten Spalten). */
  private writeFacetRow(sheet: ExcelJS.Worksheet, row: number, specName: string, facet: IDSFacet, isRequirement: boolean): void {
    this.setCell(sheet, row, 2, specName);

    switch (facet.type) {
      case 'entity':
      case 'entityGroup':
        this.setCellFromField(sheet, row, 3, facet.entityName);
        this.setCellFromField(sheet, row, 4, facet.predefinedType);
        break;
      case 'attribute':
        this.setCellFromField(sheet, row, 5, facet.attributeName);
        this.setCellFromField(sheet, row, 6, facet.attributeValue);
        break;
      case 'property':
        this.setCellFromField(sheet, row, 7, facet.propertySet);
        this.setCellFromField(sheet, row, 8, facet.propertyName);
        if (facet.propertyDataType) this.setCell(sheet, row, 9, facet.propertyDataType);
        this.setCellFromField(sheet, row, 10, facet.propertyValue);
        break;
      case 'classification':
        this.setCellFromField(sheet, row, 11, facet.classificationSystem);
        this.setCellFromField(sheet, row, 12, facet.classificationValue);
        break;
      case 'material':
        this.setCellFromField(sheet, row, 13, facet.materialName);
        break;
      case 'partOf':
        if (facet.relation) this.setCell(sheet, row, 14, facet.relation);
        this.setCellFromField(sheet, row, 15, facet.partOfEntityName);
        this.setCellFromField(sheet, row, 16, facet.partOfPredefinedType);
        break;
    }

    if (isRequirement) {
      this.setCell(sheet, row, 17, NECESSITY_TEXT[facet.metaNecessity ?? 'required']);
      if (facet.metaDescription) this.setCell(sheet, row, 18, facet.metaDescription);
      if (facet.metaInstruction) this.setCell(sheet, row, 19, facet.metaInstruction);
      if (facet.metaUri) this.setCell(sheet, row, 20, facet.metaUri);
    }
  }

  // ═══════════ Attributliste (.xlsx) ═══════════

  /**
   * Befüllt die Attributliste-Vorlage: jede Spezifikation wird einem passenden Bauteil-Tabellenblatt
   * zugeordnet (per Namensabgleich, sonst per Entitätsname, sonst wird ein neues Blatt anhand von
   * "Alle Elemente" angelegt) und dessen Datenzeilen werden durch die Eigenschafts-Anforderungen
   * der Spezifikation ersetzt. Tabellenblätter ohne passende Spezifikation bleiben unverändert
   * (inkl. ihres Beispielinhalts). Da IDS-Anforderungen kein LOI-Level kennen, werden alle
   * LOI-Spalten (100–500) markiert.
   */
  async exportAttributeList(rule: IDSRule): Promise<Blob> {
    const workbook = await this.loadTemplate(ATTRIBUTE_LIST_TEMPLATE_URL);
    const candidateSheets = workbook.worksheets.filter(s => !ATTRIBUTE_LIST_NON_COMPONENT_SHEETS.has(s.name));
    const usedSheetNames = new Set(workbook.worksheets.map(s => s.name.toLowerCase()));
    const originalRowCounts = new Map(candidateSheets.map(s => [s.name, s.rowCount] as const));
    const writeCursor = new Map<string, number>();

    for (const spec of rule.specifications) {
      const sheet = this.resolveAttributeSheet(workbook, candidateSheets, usedSheetNames, spec);
      if (!originalRowCounts.has(sheet.name)) originalRowCounts.set(sheet.name, sheet.rowCount);
      const columns = this.resolveAttributeColumns(sheet);
      const originalRowCount = originalRowCounts.get(sheet.name)!;

      if (!writeCursor.has(sheet.name)) {
        this.clearDataRows(sheet, columns.startRow, originalRowCount, 12);
        writeCursor.set(sheet.name, columns.startRow);
      }

      let row = writeCursor.get(sheet.name)!;
      for (const facet of spec.requirements) {
        if (facet.type !== 'property') continue;
        const attrName = this.fieldText(facet.propertyName);
        if (!attrName) continue;

        if (row > originalRowCount) this.copyRowStyle(sheet, columns.startRow, row);
        this.setCell(sheet, row, columns.attribute, attrName);
        const pset = this.fieldText(facet.propertySet);
        if (pset && pset !== '.*') this.setCell(sheet, row, columns.pset, pset);
        if (facet.propertyDataType) this.setCell(sheet, row, columns.dataType, facet.propertyDataType);
        const optionset = this.fieldText(facet.propertyValue);
        if (optionset) this.setCell(sheet, row, columns.optionset, optionset);
        for (const col of columns.loiColumns) this.setCell(sheet, row, col, 'X');
        row++;
      }
      writeCursor.set(sheet.name, row);
    }

    // Nur die Bauteil-Blätter behalten, die tatsächlich einer Spezifikation der aktuellen IDS
    // zugeordnet wurden - die übrigen Vorlagen-Bauteile (mit ihren Beispieldaten) fliegen raus,
    // damit die exportierte Datei ausschließlich die Anforderungen dieser IDS enthält.
    for (const sheet of [...workbook.worksheets]) {
      if (ATTRIBUTE_LIST_NON_COMPONENT_SHEETS.has(sheet.name)) continue;
      if (!writeCursor.has(sheet.name)) workbook.removeWorksheet(sheet.id);
    }

    return this.toBlob(workbook);
  }

  /** Findet das zur Spezifikation passende Bauteil-Tabellenblatt, oder legt eines anhand von "Alle Elemente" neu an. */
  private resolveAttributeSheet(
    workbook: ExcelJS.Workbook, candidateSheets: ExcelJS.Worksheet[], usedSheetNames: Set<string>, spec: IDSSpecification,
  ): ExcelJS.Worksheet {
    const normalizedSpecName = spec.name.trim().toLowerCase();
    const byName = candidateSheets.find(s => s.name.trim().toLowerCase() === normalizedSpecName);
    if (byName) return byName;

    const entityText = this.entityCellText(spec)?.toUpperCase();
    if (entityText) {
      const byEntity = candidateSheets.find(s => {
        const cellText = this.valueToText(s.getRow(2).getCell(6).value)?.toUpperCase();
        return cellText ? cellText.split('.')[0] === entityText.split('.')[0] : false;
      });
      if (byEntity) return byEntity;
    }

    const base = candidateSheets.find(s => s.name === 'Alle Elemente') ?? candidateSheets[0];
    const clone = this.cloneAttributeSheet(workbook, base, this.sanitizeSheetName(spec.name, usedSheetNames));
    candidateSheets.push(clone);
    this.setCell(clone, 1, 1, spec.name);
    this.setCell(clone, 2, 1, spec.name);
    this.setCell(clone, 2, 2, entityText ?? null);
    this.setCell(clone, 2, 6, entityText ?? null);
    return clone;
  }

  /** Kopiert Kopfzeilen (inkl. Formatierung/Verbund) eines Bauteil-Blatts, ohne dessen Datenzeilen. */
  private cloneAttributeSheet(workbook: ExcelJS.Workbook, base: ExcelJS.Worksheet, name: string): ExcelJS.Worksheet {
    const sheet = workbook.addWorksheet(name);
    base.columns.forEach((col, i) => {
      if (col?.width) sheet.getColumn(i + 1).width = col.width;
    });

    const headerRows = this.resolveAttributeColumns(base).startRow - 1;
    for (let r = 1; r <= headerRows; r++) {
      const srcRow = base.getRow(r);
      const dstRow = sheet.getRow(r);
      srcRow.eachCell({includeEmpty: true}, (cell, col) => {
        const dstCell = dstRow.getCell(col);
        dstCell.value = cell.value;
        dstCell.style = cell.style;
      });
      dstRow.height = srcRow.height;
    }

    for (const merge of base.model.merges ?? []) {
      const rows = merge.match(/\d+/g)?.map(Number) ?? [];
      if (rows.length && Math.max(...rows) <= headerRows) sheet.mergeCells(merge);
    }

    return sheet;
  }

  /** Text der (ersten) Entitäts-Facette einer Spezifikation, für den Sheet-Namen/Entity-Abgleich. */
  private entityCellText(spec: IDSSpecification): string | undefined {
    const entityFacet = spec.applicability.find(f => f.type === 'entity' || f.type === 'entityGroup');
    return entityFacet ? this.fieldText(entityFacet.entityName) : undefined;
  }

  /**
   * Löst Kopfzeile und Datenspalten eines Bauteil-Blatts auf (Kopfzeile "Attribute"/"ME"/... +
   * "LOI"-Block, siehe `IdsExcelImportService.resolveAttributeColumns`). Fällt auf das Standardlayout
   * der Vorlage zurück (Kopfzeile 4, Daten ab 6, LOI-Spalten 8–12), falls die Kopfzeile abweicht.
   */
  private resolveAttributeColumns(sheet: ExcelJS.Worksheet): AttributeSheetColumns {
    for (let row = 1; row <= sheet.rowCount; row++) {
      let attributeCol: number | null = null;
      let unitFound = false;
      let dataTypeCol = 0;
      let psetCol = 0;
      let loiCol: number | null = null;

      for (let col = 1; col <= sheet.columnCount; col++) {
        const text = this.valueToText(sheet.getRow(row).getCell(col).value)?.trim().toLowerCase();
        if (!text) continue;
        if (text === 'attribute') attributeCol = col;
        else if (text === 'me') unitFound = true;
        else if (text === 'datentyp') dataTypeCol = col;
        else if (text === 'pset') psetCol = col;
        else if (text === 'loi' && loiCol === null) loiCol = col;
      }

      if (attributeCol === null || !unitFound) continue;

      const loiColumns: number[] = [];
      if (loiCol != null) {
        for (let col = loiCol; col <= sheet.columnCount; col++) {
          const level = sheet.getRow(row + 1).getCell(col).value;
          if (typeof level === 'number' || (typeof level === 'string' && level.trim())) loiColumns.push(col);
        }
      }

      let optionsetCol = 0;
      for (let col = 1; col <= sheet.columnCount; col++) {
        if (this.valueToText(sheet.getRow(row).getCell(col).value)?.trim().toLowerCase() === 'optionset') optionsetCol = col;
      }

      return {startRow: row + 2, attribute: attributeCol, dataType: dataTypeCol, pset: psetCol, optionset: optionsetCol, loiColumns};
    }

    return {startRow: 6, attribute: 1, dataType: 3, pset: 5, optionset: 7, loiColumns: [8, 9, 10, 11, 12]};
  }

  // ═══════════ Gemeinsame Hilfsfunktionen ═══════════

  private async loadTemplate(url: string): Promise<ExcelJS.Workbook> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Vorlage konnte nicht geladen werden (${response.status}): ${url}`);
    const buffer = await response.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    return workbook;
  }

  private requireSheet(workbook: ExcelJS.Workbook, name: string): ExcelJS.Worksheet {
    const sheet = workbook.getWorksheet(name);
    if (!sheet) throw new Error(`Das Tabellenblatt "${name}" wurde in der Vorlage nicht gefunden.`);
    return sheet;
  }

  /** Leert die Werte der Datenzeilen (behält Formatierung/Rahmen der Zellen bei). */
  private clearDataRows(sheet: ExcelJS.Worksheet, fromRow: number, toRow: number, maxCol: number): void {
    for (let row = fromRow; row <= toRow; row++) {
      for (let col = 1; col <= maxCol; col++) {
        sheet.getRow(row).getCell(col).value = null;
      }
    }
  }

  /** Überträgt die Zellformatierung einer Referenzzeile auf eine Zeile jenseits des ursprünglichen Vorlagenbereichs. */
  private copyRowStyle(sheet: ExcelJS.Worksheet, referenceRow: number, targetRow: number): void {
    const ref = sheet.getRow(referenceRow);
    const target = sheet.getRow(targetRow);
    ref.eachCell({includeEmpty: true}, (cell, col) => {
      target.getCell(col).style = cell.style;
    });
    target.height = ref.height;
  }

  /** Wandelt ein IDSValueField in den Zelltext, den `IdsExcelImportService.parseCellValue` wieder erkennt. */
  private fieldText(field: IDSValueField): string | undefined {
    if (isValueFieldEmpty(field)) return undefined;
    switch (field.dataType) {
      case 'Wert':
        return field.simpleValue;
      case 'Aufzählung':
        return field.enumValues.join(';');
      case 'Muster':
        return field.pattern ? `MUSTER=${field.pattern}` : undefined;
      case 'Grenzen': {
        const parts: string[] = [];
        if (field.rangeMin) parts.push(`MIN=${field.rangeMin}`);
        if (field.rangeMax) parts.push(`MAX=${field.rangeMax}`);
        return parts.length ? parts.join(' ') : undefined;
      }
      case 'Länge':
        return field.lengthValue;
      case 'Länge Grenzen': {
        const parts: string[] = [];
        if (field.lengthMin) parts.push(`MIN=${field.lengthMin}`);
        if (field.lengthMax) parts.push(`MAX=${field.lengthMax}`);
        return parts.length ? parts.join(' ') : undefined;
      }
      default:
        return field.simpleValue;
    }
  }

  private setCellFromField(sheet: ExcelJS.Worksheet, row: number, col: number, field: IDSValueField): void {
    const text = this.fieldText(field);
    if (text !== undefined) this.setCell(sheet, row, col, text);
  }

  private setCell(sheet: ExcelJS.Worksheet, row: number, col: number, value: ExcelJS.CellValue): void {
    sheet.getRow(row).getCell(col).value = value;
  }

  private valueToText(value: ExcelJS.CellValue): string | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return null;
    if (typeof value === 'object') {
      if ('richText' in value) return value.richText.map(t => t.text).join('').trim() || null;
      if ('result' in value) return this.valueToText((value as {result: ExcelJS.CellValue}).result);
      if ('text' in value) return String((value as {text: unknown}).text).trim() || null;
      return null;
    }
    const text = String(value).trim();
    return text || null;
  }

  private parseIsoDate(value: string): Date | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return undefined;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private sanitizeSheetName(name: string, usedNames: Set<string>): string {
    const base = (name || 'Bauteil').replace(/[\\/?*[\]:]/g, '-').trim().slice(0, 31) || 'Bauteil';
    let candidate = base;
    let suffix = 2;
    while (usedNames.has(candidate.toLowerCase())) {
      const marker = ` (${suffix})`;
      candidate = base.slice(0, 31 - marker.length) + marker;
      suffix++;
    }
    usedNames.add(candidate.toLowerCase());
    return candidate;
  }

  private async toBlob(workbook: ExcelJS.Workbook): Promise<Blob> {
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {type: EXCEL_MIME_TYPE});
  }
}
